/**
 * src/core/api.ts
 * Camada de comunicação responsável por buscar dados externos e dinâmicos.
 *
 * Responsabilidades:
 * - Carregar o JSON de configuração do cenário (CDN / arquivo estático).
 * - Integrar com a API AJAX do Moodle para resolução dinâmica de links e permissões.
 * - Detectar automaticamente o ambiente (Moodle vs externo).
 *
 * Estratégia de Resiliência:
 * - FAIL OPEN por padrão na integração Moodle.
 *   Qualquer falha (rede, parse, permissão, ambiente inválido)
 *   resulta em `null`, permitindo que a UI continue funcional.
 *
 * Este módulo NÃO deve lançar exceções por falhas do Moodle.
 */

import { CenarioConfig, MoodleRawResponse, MoodleStateResponse } from './types';
import { validateConfig } from './validator';

// ---------------------------------------------------------------------------
// Declaração global do objeto `M` do Moodle
// ---------------------------------------------------------------------------
// Necessária para que o TypeScript reconheça o objeto injetado globalmente
// pelo Moodle (`window.M`). Esse objeto existe apenas quando o script é
// executado dentro do LMS.
declare global {
  interface Window {
    M?: {
      cfg: {
        wwwroot: string;
        sesskey: string;
      };
    };
  }
}

// ============================================================================
// 1. UTILITÁRIOS DE AMBIENTE
// ============================================================================

/**
 * Detecta se o código está sendo executado dentro do Moodle.
 *
 * Critério:
 * - `window` existe (ambiente browser)
 * - `window.M` foi injetado pelo Moodle
 * - `window.M.cfg` contém as configurações de sessão
 *
 * Esse método é usado como "feature detection", não como flag manual.
 */
export function isMoodleEnvironment(): boolean {
  return typeof window !== 'undefined' && !!window.M?.cfg;
}

/**
 * Extrai o ID do curso atual a partir das classes do `<body>`.
 *
 * O Moodle injeta classes como:
 * - `course-123`
 * - `context-course-123`
 *
 * Esse método evita dependência de variáveis globais não documentadas
 * e funciona de forma consistente entre versões do Moodle.
 *
 * @returns ID do curso ou `null` se não for detectável.
 */
export function getCourseId(): number | null {
  if (typeof document === 'undefined') return null;

  const match = document.body.className.match(/course-(\d+)/);
  return match ? Number(match[1]) : null;
}

/**
 * Retorna as configurações de sessão do Moodle.
 *
 * Centraliza o acesso a `window.M.cfg` para evitar espalhar
 * verificações condicionais pelo código.
 */
function getMoodleConfig() {
  return window.M?.cfg ?? { wwwroot: '', sesskey: '' };
}

// ============================================================================
// 2. CENÁRIO (JSON / CDN)
// ============================================================================

/**
 * Busca e valida o JSON de configuração do cenário.
 *
 * Fluxo:
 * 1. Realiza o fetch do JSON (CDN ou caminho relativo).
 * 2. Valida o schema e regras de negócio via `validateConfig`.
 * 3. Bloqueia renderização se houver erros críticos.
 * 4. Em DEV, exibe warnings de validação no console.
 *
 * @param url URL absoluta ou relativa do arquivo JSON.
 * @throws Error se o JSON for inválido ou a requisição falhar.
 */
export async function fetchCenarioConfig(url: string): Promise<CenarioConfig> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Erro HTTP ao carregar JSON: ${response.status}`);
  }

  const rawData = await response.json();
  const validation = validateConfig(rawData);

  if (!validation.isValid) {
    console.error('[CenarioMap] Erros de validação:', validation.errors);
    throw new Error('Configuração inválida.');
  }

  if (validation.warnings.length && import.meta.env.DEV) {
    console.warn('[CenarioMap] Avisos de validação:', validation.warnings);
  }

  return rawData as CenarioConfig;
}

// ============================================================================
// 3. INTEGRAÇÃO MOODLE (AJAX / FAIL OPEN)
// ============================================================================

/**
 * Busca o estado das atividades do curso no Moodle.
 *
 * Serviço utilizado:
 * - `core_courseformat_get_state`
 *
 * O retorno contém:
 * - lista de módulos (atividades),
 * - URLs resolvidas,
 * - flags de visibilidade e permissão (`uservisible`).
 *
 * Estratégia FAIL OPEN:
 * - Qualquer erro (ambiente, rede, parse, lógica) resulta em `null`.
 * - A camada de UI deve interpretar `null` como "sem restrições".
 *
 * @returns Estado do curso ou `null` em caso de falha.
 */
export async function fetchMoodleState(): Promise<MoodleStateResponse | null> {
  // Ambiente externo (preview, DEV, site estático)
  if (!isMoodleEnvironment()) {
    return import.meta.env.DEV ? fetchMockMoodleState() : null;
  }

  const { wwwroot, sesskey } = getMoodleConfig();
  const courseId = getCourseId();

  if (!courseId) {
    console.warn('[CenarioMap] ID do curso não detectado. Links dinâmicos desabilitados.');
    return null;
  }

  try {
    const response = await fetch(`${wwwroot}/lib/ajax/service.php?sesskey=${sesskey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([
        {
          index: 0,
          methodname: 'core_courseformat_get_state',
          args: { courseid: courseId },
        },
      ]),
    });

    if (!response.ok) {
      throw new Error(`Moodle API HTTP ${response.status}`);
    }

    return parseMoodleResponse(await response.json());
  } catch (error) {
    // FAIL OPEN: loga o erro, mas não quebra a aplicação.
    console.error('[CenarioMap] Falha na API Moodle (Fail Open).', error?.toString() ?? error);
    return null;
  }
}

// ============================================================================
// 4. HELPERS
// ============================================================================

/**
 * Carrega um mock local do estado do Moodle (apenas em DEV).
 *
 * Útil para desenvolvimento fora do LMS sem quebrar
 * a lógica de integração e sem acoplamento ao backend real.
 */
async function fetchMockMoodleState(): Promise<MoodleStateResponse | null> {
  try {
    const res = await fetch('/mocks/moodle-state.json');
    return parseMoodleResponse(await res.json());
  } catch {
    return null;
  }
}

/**
 * Decodifica a resposta "double-encoded" da API do Moodle.
 *
 * O Moodle retorna um array no formato:
 *   [{ error: boolean, data: "JSON_STRING" }]
 *
 * Onde o payload real está stringificado dentro de `data`.
 *
 * @param response Resposta bruta da API AJAX.
 * @returns Objeto parseado ou `null` em caso de erro.
 */
function parseMoodleResponse(response: unknown): MoodleStateResponse | null {
  if (!Array.isArray(response) || !response.length) return null;

  const item = response[0] as MoodleRawResponse;

  if (item.error) {
    console.error('[CenarioMap] Moodle retornou erro lógico:', item);
    return null;
  }

  try {
    return JSON.parse(item.data) as MoodleStateResponse;
  } catch (e) {
    console.error('[CenarioMap] Falha ao decodificar resposta Moodle:', e);
    return null;
  }
}
